// REACT
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// YARN
import {
  GridColDef,
  GridRowId,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'react-toastify';
// SERVICES
import { CustomSortableHeader } from 'utils/Datagrid/CustomGenericDataGridUtils';
import useDataGridCommonListActions from 'utils/Datagrid/useDataGridCommonListActions';
import { CustomHeaderWithoutSort } from 'utils/Datagrid/CustomCultureDataGridUtils';
import CustomCommonListDataGrid from 'utils/Datagrid/CustomCommonListDataGrid';
import { formatDate } from 'utils';
import { deleteTechnician, linkUserToSector } from 'services/API/Cooperative';
import useAuth from 'providers/Auth/useAuth';
// INTERFACES
import { ITechnician } from 'components/generics/Interface/Api/Response/User/ITechnician';
// THEMES
import { CustomCommonListDataGridTheme } from 'utils/Datagrid/CustomCommonListDataGridUtils';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';

type StructureTechniciansDataGridParams = {
  technicians: ITechnician[];
  refreshData: () => void;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setEditData: React.Dispatch<React.SetStateAction<GridValidRowModel | null | undefined>>;
  sector: number;
};
export const StructureTechniciansDataGrid = ({
  technicians,
  refreshData,
  setModal,
  setIsEdit,
  setEditData,
  sector,
}: StructureTechniciansDataGridParams): React.JSX.Element => {
  const [rows, setRows] = useState<GridRowsProp | []>([]);
  const [dataGridLoading, setDataGridLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [actionId, setActionId] = useState<GridRowId | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'email',
      sort: 'asc',
    },
  ]);
  const { user } = useAuth();
  const thisTechnician: GridValidRowModel | undefined = rows?.find((row) => row.id === actionId);

  type ProcessedRowType = {
    id: number;
    email: string;
    lastname: string;
    firstname: string;
    createdAt: string;
  };
  const processedRows: ProcessedRowType[] = useMemo(() => {
    return technicians?.map((technician) => {
      return {
        id: technician.id,
        email: technician.email,
        lastname: technician.lastname,
        firstname: technician.firstname,
        createdAt: technician.createdAt,
      };
    });
  }, [technicians]);

  useEffect(() => {
    setDataGridLoading(true);
    setRows(processedRows ?? []);
    if (processedRows) {
      setDataGridLoading(false);
    }
  }, [technicians]);

  const handleRemoveTechnicianFromSector = async (id: GridRowId): Promise<void> => {
    let cooperativeId: number | null = null;
    if (user?.role === 'SUPER_ADMIN') {
      cooperativeId = Number(localStorage.getItem('cooperative'));
    }

    const userToUnlink = {
      id: id,
      sectorsToRemove: [sector],
      cooperative: cooperativeId,
    };

    try {
      await linkUserToSector(userToUnlink);
      toast.success(
        t('success.technicianRemovedFromSector', {
          email: rows?.find((row) => row.id === id)?.email,
        }),
      );
      refreshData();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const { getActions } = useDataGridCommonListActions(
    processedRows,
    setModal,
    setIsEdit,
    setEditData,
    setOpenDeleteDialog,
    setActionId,
    { fromTechnicianSector: sector, handleRemoveTechnicianFromSector },
  );

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: t('label.email'),
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'email'}
          name={t('label.email')}
        />
      ),
    },
    {
      field: 'lastname',
      headerName: t('common.name'),
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'lastname'}
          name={t('common.name')}
        />
      ),
    },
    {
      field: 'firstname',
      headerName: t('form.firstname'),
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'firstname'}
          name={t('form.firstname')}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: t('label.creationDate'),
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value) => formatDate(value),
      flex: 1,
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'createdAt'}
          name={t('label.creationDate')}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions'),
      flex: 1,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => getActions(params.id),
      renderHeader: () => <CustomHeaderWithoutSort name={t('common.actions')} />,
    },
  ];

  const handleDeleteClick = async (): Promise<void> => {
    setOpenDeleteDialog(false);
    try {
      await deleteTechnician(thisTechnician);
      toast.success(t('success.technicianDeleted', { email: thisTechnician?.email }));
      refreshData();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const renderDeleteDialog = (): React.JSX.Element => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <h3>{t('title.deleteTechnician')}</h3>
            <p>{t('message.confirmDeleteTechnician')}</p>
            <p>
              <strong>{thisTechnician?.email}</strong>. {t('common.continueQuestion')}
            </p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClick}>
              {t('common.delete')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  return (
    <div>
      <div className="sector-data-grid common-list-data-grid">
        <CustomCommonListDataGrid
          rows={rows}
          columns={columns}
          isLoading={dataGridLoading}
          sortModel={sortModel}
          theme={CustomCommonListDataGridTheme}
        />
      </div>
      {renderDeleteDialog()}
    </div>
  );
};
